import React, { useEffect, useState, Suspense } from "react";
import { Button, Col, Form, Input, Row, DatePicker, Checkbox, notification, message, } from "antd";
import { Select, Typography, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { countryService, driverService } from "../../../services";
import dayjs from "dayjs";
import MaterialLikeInput from "../../../components/MaterialLikeInput";
import MaterialLikePasswordInput from "../../../components/MaterialLikeInputPassword";
import MaterialLikeSelect from "../../../components/MaterialLikeSelect";
import { changeDocumentTitle } from "../../../utils/title";

const { Title } = Typography;
const { Option } = Select;

function DriverForm() {
    const { id } = useParams();
    const isEditMode = id !== undefined;
    const navigate = useNavigate()
    const [form] = Form.useForm();
    // const [driver, setDriver] = useState({});
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(false);

    // for confirm password validation
    const [password, setPassword] = useState(false)

    const onFinish = async (values) => {
        setLoading(true)

        if (isEditMode && !values.password) {
            delete values.password
            delete values.password_confirmation
        }

        const response = isEditMode ? await driverService.update(id, values) : await driverService.add(values);
        setLoading(false)
        if (response?.success ?? false) {
            notification.success({
                message: isEditMode ? "Driver updated successfully!" : "Driver added successfully!",
                placement: "topRight",
            });
            navigate("/operations/driver")
        }
    };

    const onFinishFailed = (errorInfo) => {
        console.error('Error Info: ' + errorInfo)
    }

    const loadCountries = async () => {
        const { success, data } = await countryService.countryGet();
        if (success) {
            // setTimeout(() => {
            setCountries(data);
            setLoading(false);
            // }, 2000);
        }
    };


    const loadData = async () => {
        const hide = message.loading("Fetching Data...", 0)
        const { success, data } = await driverService.view(id);
        hide()
        if (success) {
            // setDriver(cusData);
            form.setFieldsValue({
                ...data,
            });
        }
    };

    useEffect(() => {
        changeDocumentTitle(isEditMode ? " Edit Customer" : " Add Customer");
        loadCountries()
        if (isEditMode) {
            loadData();
        }
    }, []);

    return (
        <div style={{ padding: "0 14px", border: "1px solid #ccc", borderRadius: "8px", backgroundColor: "#FFFFFF" }} >
            <Row
                gutter={26}
                style={{
                    backgroundColor: "#FBFBFB",
                    marginBottom: 15,
                    padding: "10px",
                    borderRadius: "4px",
                    borderBottom: "1px solid #E8E8E9",
                }}
            >
                <Col span={12}>
                    <h1 style={{ fontWeight: "bold" }}>
                        {isEditMode ? "Edit Driver" : "Add Driver"}
                    </h1>
                </Col>
            </Row>

            <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed}>
                <Row gutter={24}>
                    <Col span={6}>
                        <Form.Item name="name" rules={[{ required: true, message: "Please input name!" }]}>
                            <MaterialLikeInput
                                label="Name"
                                required
                                value={form.getFieldValue("name")}
                                onChange={(e) => form.setFieldValue("name", e.target.value)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item name="email"
                            rules={[
                                { required: true, message: "Please input email!" },
                                {
                                    type: "email",
                                    message: "Please enter a valid email!",
                                },
                            ]}
                        >
                            <MaterialLikeInput
                                label="Email"
                                required
                                value={form.getFieldValue("email")}
                                onChange={(e) => form.setFieldValue("email", e.target.value)}
                                autoComplete="new-password" // ✅ stops autofill
                                name="fake-email" // ✅ prevents Chrome autofill
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            name="password"
                            rules={[{ required: !isEditMode, message: "Please input password!" }]}
                            onChange={() => setPassword(!!form.getFieldValue('password'))}
                        >
                            <MaterialLikePasswordInput
                                label="Password"
                                required
                                value={form.getFieldValue("password")}
                                onChange={(e) => form.setFieldValue("password", e.target.value)}
                                autoComplete="new-password" // ✅ stops autofill
                                name="fake-email" // ✅ prevents Chrome autofill
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            name="password_confirmation"
                            dependencies={["password"]}
                            rules={[
                                {
                                    required: password,
                                    message: "Please input confirm password!",
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue("password") === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error("Passwords do not match!")
                                        );
                                    },
                                })
                            ]}
                        >
                            <MaterialLikePasswordInput
                                label="Confirm Password"
                                required
                                value={form.getFieldValue("password_confirmation")}
                                onChange={(e) => form.setFieldValue("password_confirmation", e.target.value)}
                            />
                        </Form.Item>
                    </Col>

                    {/* Other fields */}
                    {/* <Col span={6}>
                        <Form.Item label="Address" name="address" rules={[{ required: true, message: "Please input address!" }]}>
                            <Input placeholder="Enter address" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Town" name="town" rules={[{ required: true, message: "Please input town!" }]}>
                            <Input placeholder="Enter town" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="City" name="city" rules={[{ required: true, message: "Please input city!" }]}>
                            <Input placeholder="Enter city" />
                        </Form.Item>
                    </Col> */}

                    <Col span={6}>
                        <Form.Item
                            name="country"
                            rules={[{ required: true, message: "Please select a country!" }]}
                        >
                            <MaterialLikeSelect
                                label="Country"
                                required
                                value={form.getFieldValue("country")}
                                onChange={(value) => form.setFieldValue('country', value)}
                                options={countries.map((country) => ({
                                    id: country.id,
                                    value: country.iso_code,
                                    label: country.name,
                                }))}
                            />
                            {/* <Select
                                showSearch
                                placeholder="Select a country"
                                optionFilterProp="children"
                                dropdownRender={(menu) => {
                                    if (loading) {
                                        return (
                                            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                                                <Spin size="small" />
                                            </div>
                                        );
                                    }
                                    return menu;
                                }}
                            >
                                {countries.map((country) => (
                                    <Select.Option key={country.id} value={country.iso_code} >
                                        {country.name}
                                    </Select.Option>
                                ))}
                            </Select> */}
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item name="phone" rules={[{ required: true, message: "Please input phone number!" }]}>
                            <MaterialLikeInput
                                label="Phone"
                                required
                                value={form.getFieldValue("name")}
                                onChange={(e) => form.setFieldValue("name", e.target.value)}
                            />
                        </Form.Item>
                    </Col>

                    {/* <Col span={6}>
                        <Form.Item label="Contact Start" name="contract_start_date">
                            <DatePicker style={{ width: "100%" }} placeholder="mm/dd/yyyy" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Contact End" name="contract_end_date">
                            <DatePicker style={{ width: "100%" }} placeholder="mm/dd/yyyy" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Contract Days" name="contract_days">
                            <Input type="number" placeholder="0" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Contact Charges/day" name="contract_charges">
                            <Input type="number" placeholder="0" />
                        </Form.Item>
                    </Col> */}

                </Row>


                <Suspense fallback={<Spin />}>
                    <Row gutter={24} style={{ padding: 20 }}>
                        {/* Add Customer Button */}
                        <Col span={24} style={{ textAlign: "center" }}>
                            <Button
                                type="primary"
                                style={{ width: "50%" }}
                                loading={loading}
                                // htmlType="submit"
                                onClick={() => form.submit()}
                            >
                                {isEditMode ? "Update Driver" : "Add Driver"}
                            </Button>
                        </Col>
                    </Row>
                </Suspense>

            </Form>
        </div >
    );
}

export default DriverForm;